/**
 * @file AuthContext.js
 * @description Global authentication state provider for FleetVelocity.
 *
 * ## System Context
 * Single source of truth for the logged-in user across all four portals
 * (Admin, Dealer, LCO, Customer).  Every portal layout and protected route
 * reads from this context to decide whether to render content or redirect
 * to the login screen.
 *
 * ## Design Rationale — C2 (Cycle-02 HttpOnly Cookie Migration)
 * Prior to C2, the access token lived in localStorage under the keys
 * `fv_token` and `fv_user`.  This exposed the token to XSS: any injected
 * script could read and exfiltrate it.
 *
 * After C2:
 *   - The backend sets `fv_access_token` and `fv_refresh_token` as HttpOnly
 *     cookies on login / MFA verification.  JavaScript cannot read them.
 *   - AuthContext no longer manages a token string.  It only manages the
 *     `user` object obtained by calling `GET /api/auth/me`.
 *   - On app mount, `/api/auth/me` is called with `withCredentials: true`
 *     (configured in api.js).  The browser sends the cookie automatically.
 *     If the cookie is valid the user object is returned and state is hydrated.
 *     If not, `user` stays null — the portal layout shows the login screen.
 *   - The api.js interceptor silently refreshes the access token on 401.
 *     If the refresh also fails it dispatches `fv:session-expired`.
 *     This context listens for that event and clears `user` to trigger login.
 *
 * ## Critical Constraints
 *   - `loading` starts as `true` and flips to `false` only after the initial
 *     `/api/auth/me` call settles.  Portal layouts MUST wait for
 *     `loading === false` before redirecting to login — otherwise they will
 *     flash the login screen on every page refresh.
 *   - `logout` MUST call `POST /api/auth/logout`.  Simply setting
 *     `user = null` leaves valid cookies in the browser and an active session
 *     in the DB.  The endpoint clears cookies (C2) and revokes the session (H11).
 *
 * ## External Dependencies
 *   - api.js              : Axios instance with withCredentials + 401 interceptor
 *   - Backend GET  /api/auth/me      : Cookie-authenticated profile endpoint
 *   - Backend POST /api/auth/logout  : Clears cookies + revokes DB session (H11)
 *   - CustomEvent 'fv:session-expired': Dispatched by api.js on refresh failure
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api, { setLogoutMode, resetSessionState } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // C2: No localStorage reads.  `user` is hydrated via /api/auth/me on mount.
  const [user, setUser] = useState(null);

  // NOTE: `loading` must start true.  If it defaulted to false, portals would
  // see `isAuthenticated = false` briefly on page load and redirect to login
  // before the /api/auth/me call returns.
  const [loading, setLoading] = useState(true);

  // ── C2: Hydrate auth state from the HttpOnly cookie ───────────────────────
  // Called once on mount.  Axios sends the cookie automatically.
  // A 401 response simply means the user is not logged in — not an error.
  useEffect(() => {
    api.get('/auth/me')
      .then(res  => setUser(res.data))
      .catch(()  => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // ── C2: Listen for session-expired signal from api.js ─────────────────────
  // Dispatched when both the access token AND the refresh token are expired
  // or revoked.  No silent refresh is possible — user must re-authenticate.
  useEffect(() => {
    const handleExpired = () => setUser(null);
    window.addEventListener('fv:session-expired', handleExpired);
    return () => window.removeEventListener('fv:session-expired', handleExpired);
  }, []);

  // ── Safety net: swallow known post-logout / post-expiry rejections ─────────
  // After a session ends, component teardown and pending effects may fire API
  // calls that get 401.  The interceptor marks these with `fvSessionEnded` or
  // `fvSessionExpired`.  Without this handler they reach the React overlay.
  useEffect(() => {
    const swallowSessionErrors = (event) => {
      const reason = event.reason;
      if (reason && (reason.fvSessionEnded || reason.fvSessionExpired)) {
        event.preventDefault(); // suppress the React uncaught-rejection overlay
      }
    };
    window.addEventListener('unhandledrejection', swallowSessionErrors);
    return () => window.removeEventListener('unhandledrejection', swallowSessionErrors);
  }, []);

  /**
   * Authenticate with email + password.
   *
   * C2: On success the backend writes HttpOnly cookies; the response body
   * contains only the user object (no token string).
   * If MFA is required, the response contains `requires_mfa: true` and a
   * `temp_token` for the MFA step.
   *
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{requires_mfa: boolean, user?: object, temp_token?: string}>}
   */
  const login = useCallback(async (email, password, portal, remember = true) => {
    // Clear logout/session-expired gates before the password call so a 401
    // from bad credentials keeps its API detail for the login form.
    resetSessionState();
    // remember: "Keep me signed in on this computer"; off, the session ends with the browser (QA DP-03)
    const res  = await api.post('/auth/login', { email: (email || '').trim(), password, portal, remember });
    const data = res.data;
    // C2: access_token is in an HttpOnly cookie — not in the response body.
    // requires_mfa only when MFA is required for the user (organisation setting, or an
    // authenticator they set up); otherwise the session cookies are already set.
    if (!data.requires_mfa && !data.requires_code) {
      setUser(data.user);
      resetSessionState();
    }
    return data;
  }, []);

  /**
   * Complete the TOTP MFA challenge (called from PortalLogin.js MFA step).
   * On success the backend issues HttpOnly cookies and returns the user object.
   *
   * @param {string} tempToken - Short-lived token from the login response
   * @param {string} code      - 6-digit TOTP code from the authenticator app
   */
  /** PRD two-step sign-in: verify the 6-digit code emailed after the password step. */
  const verifyCode = useCallback(async (tempToken, code, remember = true) => {
    const res = await api.post('/auth/verify-code', { temp_token: tempToken, code, remember });
    setUser(res.data.user);
    resetSessionState();
    return res.data;
  }, []);

  /** Ask for a new sign-in code (30-second cooldown server-side). */
  const resendCode = useCallback(async (tempToken) => {
    const res = await api.post('/auth/resend-code', { temp_token: tempToken });
    return res.data;
  }, []);

  const verifyMFA = useCallback(async (tempToken, code, remember = true) => {
    const res  = await api.post('/auth/verify-mfa', { temp_token: tempToken, code, remember });
    const data = res.data;
    setUser(data.user);
    resetSessionState(); // re-arm the interceptor for the new session
    return data;
  }, []);

  /**
   * Begin MFA enrollment: generates a TOTP secret + QR code.
   * Does NOT set auth state — enrollment must be confirmed via confirmEnrollment.
   *
   * @param {string} tempToken - Short-lived token from the login response
   */
  const enrollMFA = useCallback(async (tempToken) => {
    const res = await api.post('/auth/enroll-mfa', { temp_token: tempToken });
    return res.data;
  }, []);

  /**
   * Confirm MFA enrollment by verifying the first TOTP code from the app.
   * On success the backend issues HttpOnly cookies and returns the user object.
   *
   * @param {string} tempToken - Short-lived token from the login response
   * @param {string} code      - First 6-digit TOTP code from the authenticator
   */
  const confirmEnrollment = useCallback(async (tempToken, code, remember = true) => {
    const res  = await api.post('/auth/confirm-enrollment', { temp_token: tempToken, code, remember });
    const data = res.data;
    setUser(data.user);
    resetSessionState(); // re-arm the interceptor for the new session
    return data;
  }, []);

  /**
   * Sign in via SSO (Google or Microsoft).
   * The backend SSO route sets the same HttpOnly cookies as a standard login.
   * We just need to update the user state from the response body.
   *
   * @param {object} ssoData - Response from /api/auth/sso/* (contains .user)
   */
  const loginWithSSO = useCallback(async (ssoData) => {
    if (ssoData?.user) {
      setUser(ssoData.user);
      resetSessionState();
    }
    return ssoData;
  }, []);

  /**
   * Re-fetch the user profile to pick up server-side changes
   * (e.g., an admin updates the user's role or organisation).
   *
   * @returns {Promise<object|null>} Updated user object or null on error
   */
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  /**
   * Sign out.
   *
   * NOTE: We MUST call POST /api/auth/logout — not just setUser(null).
   * The endpoint clears the HttpOnly cookies (C2) and revokes the server-side
   * UserSession row (H11).  Without calling it, cookies remain in the browser
   * and the session stays active in the DB.
   *
   * Logout-mode gate (C2 fix — post-logout 401 overlay):
   *   setLogoutMode(true) BEFORE the API call arms the interceptor to silently
   *   absorb any in-flight requests that get 401 after cookies are cleared.
   *   Without this, those 401s each attempt a refresh → refresh fails →
   *   Promise.reject cascades → React error overlay on the login page.
   *
   * Best-effort: even on network error we clear local state so the UI shows
   * the login screen.  The cookies will expire on their own (8 h / 7 d).
   */
  const logout = useCallback(async () => {
    // ── Gate: stop the interceptor from retrying 401s during teardown ─────────
    setLogoutMode(true);
    try {
      await api.post('/auth/logout');
    } catch {
      // Best-effort — clear local state regardless of network errors.
    }
    setUser(null);
    // NOTE: resetSessionState() is intentionally NOT called here.
    // The gate stays armed until the next successful login so that
    // any component teardown API calls (which fire AFTER setUser(null))
    // are also silently absorbed.
  }, []);

  // C2: isAuthenticated is derived purely from user presence — no token needed.
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      verifyMFA,
      enrollMFA,
      confirmEnrollment,
      loginWithSSO,
      logout,
      refreshUser,
      verifyCode,
      resendCode,
      isAuthenticated,
      // NOTE: `token` is intentionally absent.
      // Components must NOT read auth tokens from context or localStorage.
      // The browser cookie is managed automatically — use api.js to call the API.
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Whether the signed-in user's organisation role may do something (backend services/role_policy.py):
 * 'campaigns', 'paid_status', 'lco_admin' or 'lco_write'. True when unknown, the backend still checks. */
export function useCan(cap) {
  const ctx = useContext(AuthContext);
  return ctx?.user?.can?.[cap] !== false;
}

/** Licence package features (PRD §4, QA AD-01): `has(code, ...)` is true when any of the codes (D-01…D-09,
 * L-01…L-10) is in the organisation's package, or when /api/auth/me gives no list (no package: everything). */
export function useFeatures() {
  const ctx = useContext(AuthContext);
  const list = ctx?.user?.features;
  return useCallback((...codes) => !Array.isArray(list) || codes.some(c => list.includes(c)), [list]);
}

export function useFeature(...codes) {
  return useFeatures()(...codes);
}
